import {useCallback, useState} from 'react';

const NONE_EXPANDED: ReadonlySet<string> = new Set<string>();

/**
 * Expand state for the Timesheet Entries person rows, held at page level.
 *
 * It cannot live in `PersonLine`: with the `timesheet_entries_optimization` flag on, each row is
 * wrapped in a `VisibilitySensor` that replaces `<PersonLine>` with a placeholder `<tr>` once the
 * row leaves the viewport, which unmounts the row and takes any local state with it. Keeping the
 * set here — above the sensor — is the same move PIKA-627 made for the audit-log drawer's
 * `openAuditLogPersonId`. (PIKA-728)
 */

/**
 * Whether a person row renders for real, rather than as the visibility placeholder.
 *
 * `isPinned` covers everything that must outlive scrolling out of view: an expanded row, and a row
 * whose audit-log drawer is open. The drawer is portalled to `document.body` but still lives in the
 * row's React subtree, so letting the placeholder replace the row would unmount it mid-read.
 *
 * `isVisible` is nullable: react-visibility-sensor initialises its state to `null` and hands that
 * straight to the children function, so a row's first render — before the sensor's first check runs
 * on mount — arrives here as `null`. That counts as not-yet-visible, matching the behaviour before
 * this fix.
 */
export const shouldRenderPersonRow = ({isVisible, isPinned}: {isVisible: boolean | null; isPinned: boolean}): boolean =>
	isVisible === true || isPinned;

/**
 * Whether a person's project rows are actually showing.
 *
 * Gated on `hasEntries` because an expanded row with nothing under it is a dead end: the caret
 * points up at an empty block. The caller decides what counts — a deactivated person's entries
 * render nothing (`EntityLine` bails on `!person.active`), so they do not count either.
 */
export const isPersonRowExpanded = ({hasEntries, isExpanded}: {hasEntries: boolean; isExpanded: boolean}): boolean =>
	hasEntries && isExpanded;

/** Flip one person's expand state, returning a new Set so React sees the change. */
export const togglePersonExpansion = (expandedPersonIds: ReadonlySet<string>, personId: string): Set<string> => {
	const next = new Set(expandedPersonIds);
	if (next.has(personId)) {
		next.delete(personId);
	} else {
		next.add(personId);
	}
	return next;
};

/**
 * Drop every expanded id that is not in `allowedIds`, or return the set unchanged when there is
 * nothing to drop — so callers can run this on every render without causing one.
 */
export const retainPersonExpansion = (
	expandedPersonIds: ReadonlySet<string>,
	allowedIds: ReadonlySet<string>
): ReadonlySet<string> => {
	const next = new Set<string>();
	expandedPersonIds.forEach(id => {
		if (allowedIds.has(id)) {
			next.add(id);
		}
	});
	return next.size === expandedPersonIds.size ? expandedPersonIds : next;
};

type PersonRowExpansion = {
	togglePersonExpanded: (personId: string) => void;
	isPersonExpanded: (personId: string) => boolean;
	retainExpanded: (allowedIds: ReadonlySet<string>) => void;
};

/**
 * @param resetKey the expanded set is dropped whenever this changes. Every expanded row is pinned
 *   as mounted by `shouldRenderPersonRow`, and an explicit collapse is otherwise the only thing
 *   that ever releases one — so keying on the viewed week caps the pinned set at the people
 *   expanded in the week currently on screen. A person's rows change completely between weeks, so
 *   carrying expansion across a week change buys nothing.
 *
 *   Callers that do not pin rows (the flag-off path) should pass a constant, so their pre-existing
 *   behaviour — expansion surviving week navigation — is left alone.
 *
 *   **Residual, accepted deliberately:** this bounds pinning *across* weeks, not *within* one.
 *   Inside a single week only an explicit collapse or a `retainExpanded` prune releases a pinned
 *   row, so someone expanding their way down a very large account accumulates mounted subtrees that
 *   re-render on every socket refetch. Within-a-week eviction (an LRU cap, or releasing a row some
 *   delay after it scrolls away) was judged out of proportion to a P3 fix. If this page is ever
 *   reported slow on a large account, start here. (PIKA-728)
 */
export const usePersonRowExpansion = (resetKey: string): PersonRowExpansion => {
	const [state, setState] = useState<{resetKey: string; ids: ReadonlySet<string>}>(() => ({
		resetKey,
		ids: NONE_EXPANDED,
	}));

	// Written, not merely masked: masking alone left the previous week's ids in state, so navigating
	// away and back matched `resetKey` again and resurrected them. Adjusted during render rather
	// than in an effect because on React 16 effects fire after paint, which would show one frame of
	// the previous week's expansion.
	if (state.resetKey !== resetKey) {
		setState({resetKey, ids: NONE_EXPANDED});
	}
	const expandedPersonIds = state.resetKey === resetKey ? state.ids : NONE_EXPANDED;

	const togglePersonExpanded = useCallback(
		(personId: string) => {
			setState(current => ({
				resetKey,
				ids: togglePersonExpansion(current.resetKey === resetKey ? current.ids : NONE_EXPANDED, personId),
			}));
		},
		[resetKey]
	);

	// Rows that stop being rendered — filtered out, or their last entry for the week deleted — must
	// not keep their id, or they would come back expanded and pinned without anyone clicking.
	const retainExpanded = useCallback(
		(allowedIds: ReadonlySet<string>) => {
			setState(current => {
				if (current.resetKey !== resetKey) {
					return current;
				}
				const ids = retainPersonExpansion(current.ids, allowedIds);
				return ids === current.ids ? current : {resetKey, ids};
			});
		},
		[resetKey]
	);

	const isPersonExpanded = useCallback((personId: string) => expandedPersonIds.has(personId), [expandedPersonIds]);

	return {togglePersonExpanded, isPersonExpanded, retainExpanded};
};
